import { useCallback, useEffect, useMemo, useState } from "react"
import { Box, Menu, MenuItem, PaperProps, PopoverOrigin, Stack, styled, Tooltip } from "@mui/material"
import { useLocalization } from "@tm/localization"
import { ArticleActionButton, ArticleActionIcon, ErpIconType, FavoriteArticleList } from "@tm/models"
import { Checkbox } from "../../muiStyled/Inputs/Checkbox"
import { Icon } from "../../muiStyled/DataDisplay/Icons"
import { LoadingContainer } from "../LoadingContainer"
import { Button } from "../../muiStyled/Inputs/button"
import { Divider } from "../../muiStyled/DataDisplay/divider"
import { TextField } from "../../muiStyled/Inputs/textfield"
import { Typography } from "../../muiStyled/DataDisplay/typographie"
import { useDefaultArticleItemState } from "./ContextProviderStates/DefaultArticleItemState"
import { ButtonAction } from "./Actions/ButtonAction"
import { IconAction } from "./Actions/IconAction"
import { ActionMenuItem } from "./Actions/ActionMenuItem"

const CustomMenuEntry = styled(MenuItem)({
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: "6px",
})

const StyledTooltip = styled(Tooltip)({
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
})

type MenuProps = {
    hiddenMenu: boolean
}

const StyledMenu = styled(Menu, { shouldForwardProp: (prop: string) => !["hiddenMenu"].includes(prop) })<MenuProps>(({ hiddenMenu }) => ({
    maxHeight: "600px",
    maxWidth: "480px",
    opacity: hiddenMenu ? 0 : 1,
    transition: "opacity 0.1s ease-in-out",
}))

const SINGLE_FAVORITE_ARTICLE_LIST_NAME = "FavoriteArticleList"
// The default value for a Mui Menu
const MENU_MARGIN_THRESHOLD = 16
const NEW_LIST_MIN_WIDTH = "200px"

type FavoriteArticleListProps = {
    hideText?: boolean
    onClose?(): void
}

export function FavoriteArticles(props: FavoriteArticleListProps) {
    const favoriteArticleList = useDefaultArticleItemState((x) => x.favoriteArticleList)
    if (!favoriteArticleList) {
        return null
    }
    return <FavoriteArticleListComponent favoriteArticleList={favoriteArticleList} {...props} />
}

function FavoriteArticleListComponent({
    favoriteArticleList,
    hideText,
    onClose,
}: FavoriteArticleListProps & { favoriteArticleList: FavoriteArticleList }) {
    const {
        isInFavoriteArticleList,
        favoriteArticleListInfos: initialFavoriteArticles,
        toggleFavoriteArticleList,
        createFavoriteArticleList,
        isToggling,
        isLoading,
        isCreating,
        createOrAddFavoriteArticleList,
        singleFavoriteArticleList,
    } = favoriteArticleList
    const { translateText } = useLocalization()
    const [creationMode, setCreationMode] = useState(false)
    const [newListName, setNewListName] = useState("")
    const [favoriteArticleLists, setFavoriteArticleLists] = useState(initialFavoriteArticles ?? [])
    const { highlightFavoriteArticlesButton, tileView } = useDefaultArticleItemState((x) => x.options)
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const [horizontalOrigin, setHorizontalOrigin] = useState<{ anchor: PopoverOrigin["horizontal"]; transform: PopoverOrigin["horizontal"] }>({
        anchor: "left",
        transform: "left",
    })
    const [isSelected, setIsSelected] = useState(false)
    const [hiddenMenu, setHiddenMenu] = useState(false)

    const open = Boolean(anchorEl)

    useEffect(() => {
        // Only set the watchlist when we have the new watchlist available
        if (initialFavoriteArticles && initialFavoriteArticles?.length > 0) {
            setFavoriteArticleLists(initialFavoriteArticles)
        }
    }, [initialFavoriteArticles])

    const renderWatchLists = useMemo(
        () =>
            favoriteArticleLists.map((listEntry) => {
                return (
                    <CustomMenuEntry key={listEntry.id} onClick={() => toggleFavoriteArticleList(listEntry.id)} disabled={listEntry.disabled}>
                        {listEntry.hasErrors ? (
                            <Tooltip title={translateText(1261)}>
                                <Icon name="danger" color="error" size="extrasmall" sx={{ px: "10px" }} />
                            </Tooltip>
                        ) : (
                            <Checkbox size="small" checked={listEntry.selected} />
                        )}
                        <StyledTooltip title={listEntry.name}>
                            <Typography>{listEntry.name}</Typography>
                        </StyledTooltip>
                    </CustomMenuEntry>
                )
            }),
        [toggleFavoriteArticleList, translateText, favoriteArticleLists]
    )

    const handleTransitionEntered = useCallback(
        (node: HTMLElement) => {
            if (!tileView || !anchorEl) {
                return
            }

            const rectRight = anchorEl.getBoundingClientRect().right
            const isAtLeftEdge = window.innerWidth - rectRight < node.getBoundingClientRect().width + MENU_MARGIN_THRESHOLD
            setHorizontalOrigin(isAtLeftEdge ? { anchor: "left", transform: "right" } : { anchor: "right", transform: "left" })
            setIsSelected(true)
            setHiddenMenu(false)
        },
        [tileView, anchorEl]
    )

    return useMemo(() => {
        const handleClick = (event?: React.MouseEvent<HTMLElement>) => {
            if (singleFavoriteArticleList) {
                createOrAddFavoriteArticleList(SINGLE_FAVORITE_ARTICLE_LIST_NAME)
            } else {
                event && setAnchorEl(event.currentTarget)
            }
            setHiddenMenu(!!tileView)
        }
        const handleClose = () => {
            setCreationMode(false)
            setAnchorEl(null)
            onClose?.()
        }

        const createWatchlist = () => {
            createFavoriteArticleList(newListName)
            setNewListName("")
            setCreationMode(false)
        }

        const action = {
            iconType: ErpIconType.FavoriteArticles,
            displayType: hideText ? "ICON" : "BUTTON",
            icon: isInFavoriteArticleList ? "filled-star" : "vote-star",
            sort: -1,
            tooltip: translateText(14069),
            text: translateText(13905),
            isHighlight: highlightFavoriteArticlesButton,
            handler: handleClick,
        }

        const renderAction = () => {
            if (tileView) {
                return <ActionMenuItem action={action as ArticleActionButton} handleCloseMore={() => null} isSelected={isSelected} />
            }
            if (hideText) {
                return <IconAction action={action as ArticleActionIcon} />
            }
            return <ButtonAction action={action as ArticleActionButton} />
        }

        const paperProps: Partial<PaperProps> | undefined = tileView
            ? {
                  sx: (theme) => ({ border: "1px solid", borderColor: theme.palette.primary.main }),
              }
            : undefined

        return (
            <Box>
                {renderAction()}
                <StyledMenu
                    anchorEl={anchorEl}
                    anchorOrigin={{ vertical: "top", horizontal: horizontalOrigin.anchor }}
                    transformOrigin={{ vertical: "top", horizontal: horizontalOrigin.transform }}
                    marginThreshold={MENU_MARGIN_THRESHOLD}
                    open={open}
                    onClose={handleClose}
                    MenuListProps={{
                        "aria-labelledby": "basic-button",
                    }}
                    PaperProps={paperProps}
                    TransitionProps={{
                        onEntered: handleTransitionEntered,
                    }}
                    hiddenMenu={hiddenMenu}
                >
                    <LoadingContainer isLoading={isToggling || isLoading || isCreating}>{renderWatchLists}</LoadingContainer>
                    <Divider />
                    {creationMode ? (
                        <MenuItem disableRipple onKeyDown={(e: React.KeyboardEvent) => e.stopPropagation()}>
                            <Stack direction="row" spacing="4px" minWidth={NEW_LIST_MIN_WIDTH}>
                                <TextField
                                    disabled={isCreating}
                                    sx={{ marginTop: 0 }}
                                    onChange={(e) => setNewListName(e.currentTarget.value)}
                                    value={newListName}
                                />
                                <Button disabled={isCreating} onClick={handleClose} startIcon={<Icon name="cancel-x" />} />
                                <Button disabled={isCreating} onClick={createWatchlist} startIcon={<Icon name="check" />} color="success" />
                            </Stack>
                        </MenuItem>
                    ) : (
                        <MenuItem>
                            <Box onClick={() => setCreationMode(true)} display="flex" alignItems="center" minWidth={NEW_LIST_MIN_WIDTH}>
                                <Icon name="plus" sx={{ marginRight: "4px" }} /> <Typography>{translateText(1242)}</Typography>
                            </Box>
                        </MenuItem>
                    )}
                </StyledMenu>
            </Box>
        )
    }, [
        hideText,
        isInFavoriteArticleList,
        translateText,
        highlightFavoriteArticlesButton,
        hiddenMenu,
        anchorEl,
        horizontalOrigin.anchor,
        horizontalOrigin.transform,
        open,
        tileView,
        handleTransitionEntered,
        isToggling,
        isLoading,
        isCreating,
        renderWatchLists,
        creationMode,
        newListName,
        singleFavoriteArticleList,
        createOrAddFavoriteArticleList,
        onClose,
        createFavoriteArticleList,
        isSelected,
    ])
}
